import React, { useEffect, useState } from 'react';
import axios from 'axios';

// Shared pricing state for the marketing pages. Fetches /api/plans once, holds
// the visitor's currency (saved choice → geo-suggested → GBP), and exposes a
// symbol + per-plan price lookup. Both the home pricing block and the pricing
// page use this so they always agree on currency (bridged via localStorage).

const CURRENCY_KEY = 'rs-currency';
const readSaved = () => {
  try { return window.localStorage.getItem(CURRENCY_KEY) || ''; } catch (e) { return ''; }
};

export function usePricing() {
  const [plans, setPlans] = useState([]);
  const [currencies, setCurrencies] = useState([]);
  const [suggested, setSuggested] = useState('');
  const [chosen, setChosen] = useState(readSaved);

  useEffect(() => {
    let cancelled = false;
    axios.get('/api/plans')
      .then((r) => {
        if (cancelled) return;
        const d = r.data || {};
        setPlans(d.plans || []);
        setCurrencies(d.currencies || []);
        if (d.suggestedCurrency) setSuggested(d.suggestedCurrency);
      })
      .catch(() => { /* leave empty — callers render their fallback */ });
    return () => { cancelled = true; };
  }, []);

  // Saved choice wins; then the geo suggestion; then GBP for rendering.
  const currency = chosen || suggested || 'gbp';
  const symbol = (currencies.find((c) => c.code === currency) || {}).symbol || '£';

  const setCurrency = (code) => {
    setChosen(code);
    try { window.localStorage.setItem(CURRENCY_KEY, code); } catch (e) { /* private mode */ }
  };

  const planByKey = (key) => plans.find((p) => p.key === key);

  return { plans, currencies, currency, symbol, setCurrency, planByKey };
}

// Segmented £/€/$ toggle, reused by both marketing pricing surfaces. Hidden
// until there's more than one currency to choose from.
export function CurrencyToggle({ currencies, currency, setCurrency }) {
  if (!currencies || currencies.length < 2) return null;
  return (
    <div className="billing-toggle" role="tablist" aria-label="Currency">
      {currencies.map((c) => (
        <button
          key={c.code}
          role="tab"
          aria-selected={currency === c.code}
          className={`billing-btn ${currency === c.code ? 'is-active' : ''}`}
          onClick={() => setCurrency(c.code)}
          title={c.label}
        >
          {c.symbol} {c.label}
        </button>
      ))}
    </div>
  );
}
